import request from "superagent";
import { getJwtToken } from "./user";
import { refreshCredentials } from "../utils/securityUtil";
import env from "../constants/env";
import state from "../state/state";

const NOTIFICATIONS_PATH = ["appState", "notifications"];
const DEFAULT_INBOX_LIMIT = 50;

function getNotificationEndpoints() {
	return (env.apiGateway && env.apiGateway.endpoints && env.apiGateway.endpoints.notifications) || {};
}

function getNotificationEndpoint(endpoint) {
	if (!endpoint) {
		throw new Error("Notification endpoint is not configured for this environment.");
	}
	return `${env.apiGateway.baseUrl}${endpoint}`;
}

function resolveNotificationEndpoint(endpointName, ...args) {
	const endpoints = getNotificationEndpoints();
	const endpoint = endpoints[endpointName];
	if (typeof endpoint === "function") {
		return endpoint(...args);
	}
	return endpoint;
}

function appendQueryString(endpoint, params = {}) {
	const query = Object.keys(params).filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== "").map((key) => {
		return `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
	}).join("&");

	return query ? `${endpoint}?${query}` : endpoint;
}

function getErrorMessage(err) {
	if (err && err.response && err.response.body && err.response.body.error) {
		return err.response.body.error;
	}
	if (err && err.response && err.response.body && err.response.body.message) {
		return err.response.body.message;
	}
	if (err && err.message) {
		return err.message;
	}
	return "Notification request failed.";
}

function getNotificationState() {
	return state.get(NOTIFICATIONS_PATH) || {};
}

function setNotificationState(patch) {
	state.set(NOTIFICATIONS_PATH, {
		...getNotificationState(),
		...patch
	});
}

function getNotificationItems() {
	return getNotificationState().items || [];
}

function getUnreadCount(items) {
	return (items || []).filter((item) => !item.readAt && !item.dismissedAt).length;
}

function getTimestamp() {
	return new Date().toISOString();
}

function updateRecipient(recipientId, patch) {
	const items = getNotificationItems().map((item) => {
		if (item.recipientId === recipientId) {
			return {
				...item,
				...patch
			};
		}
		return item;
	});

	setNotificationState({
		items,
		unreadCount: getUnreadCount(items)
	});
}

function removeRecipient(recipientId) {
	const items = getNotificationItems().filter((item) => item.recipientId !== recipientId);
	setNotificationState({
		items,
		unreadCount: getUnreadCount(items)
	});
}

async function sendAuthorizedRequest(method, endpoint, payload) {
	let promise = new Promise((resolve, reject) => {
		refreshCredentials().then(() => {
			const authorizedRequest = request[method](getNotificationEndpoint(endpoint))
				.set({ Authorization: getJwtToken(), "Content-Type": "application/json" });

			if (payload !== undefined) {
				authorizedRequest.send(payload);
			}

			authorizedRequest
				.then((res) => {
					resolve(res.body);
				}).catch(err => {
					console.error(err);
					reject(err);
				});
		}).catch(err => {
			reject(err);
		});
	});
	return await promise;
}

async function getAuthorizedRequest(endpoint) {
	let promise = new Promise((resolve, reject) => {
		refreshCredentials().then(() => {
			request.get(getNotificationEndpoint(endpoint))
				.set({ Authorization: getJwtToken(), "Content-Type": "application/json" })
				.then((res) => {
					resolve(res.body);
				}).catch(err => {
					console.error(err);
					reject(err);
				});
		}).catch(err => {
			reject(err);
		});
	});
	return await promise;
}

export async function sendNotificationMessage(message) {
	const endpoints = getNotificationEndpoints();
	return await sendAuthorizedRequest("post", endpoints.messages, message);
}

export async function sendNotificationInboxMessage(message) {
	const endpoints = getNotificationEndpoints();
	return await sendAuthorizedRequest("post", endpoints.inbox, message);
}

export function clearNotificationState() {
	setNotificationState({
		visible: false,
		loading: false,
		refreshing: false,
		error: "",
		items: [],
		unreadCount: 0,
		lastLoadedAt: ""
	});
}

export function setNotificationCenterVisible(visible) {
	state.set([...NOTIFICATIONS_PATH, "visible"], visible);
}

export async function showNotificationCenter() {
	setNotificationCenterVisible(true);
	return await fetchNotificationInbox();
}

export function hideNotificationCenter() {
	setNotificationCenterVisible(false);
}

export async function fetchNotificationInbox(options = {}) {
	const {
		limit = DEFAULT_INBOX_LIMIT,
		offset = 0,
		includeDismissed = false,
		silent = false
	} = options;
	const endpoint = appendQueryString(resolveNotificationEndpoint("inbox"), {
		limit,
		offset,
		includeDismissed: includeDismissed ? "true" : undefined
	});

	setNotificationState({
		loading: !silent,
		refreshing: !!silent,
		error: ""
	});

	try {
		const response = await getAuthorizedRequest(endpoint);
		const items = Array.isArray(response.notifications) ? response.notifications : [];
		const unreadCount = typeof response.unreadCount === "number" ? response.unreadCount : getUnreadCount(items);
		setNotificationState({
			loading: false,
			refreshing: false,
			error: "",
			items,
			unreadCount,
			lastLoadedAt: getTimestamp()
		});
		return response;
	} catch (err) {
		setNotificationState({
			loading: false,
			refreshing: false,
			error: getErrorMessage(err)
		});
		throw err;
	}
}

export async function refreshNotificationUnreadCount() {
	const endpoint = resolveNotificationEndpoint("unreadCount");
	const response = await getAuthorizedRequest(endpoint);
	const unreadCount = typeof response.unreadCount === "number" ? response.unreadCount : 0;
	state.set([...NOTIFICATIONS_PATH, "unreadCount"], unreadCount);
	return response;
}

export async function markNotificationRead(notification) {
	const recipientId = notification && notification.recipientId;
	if (!recipientId) {
		throw new Error("Notification recipient id is required.");
	}
	const response = await sendAuthorizedRequest("put", resolveNotificationEndpoint("read", recipientId));
	updateRecipient(recipientId, {
		readAt: notification.readAt || getTimestamp()
	});
	return response;
}

export async function dismissNotification(notification) {
	const recipientId = notification && notification.recipientId;
	if (!recipientId) {
		throw new Error("Notification recipient id is required.");
	}
	const response = await sendAuthorizedRequest("put", resolveNotificationEndpoint("dismiss", recipientId));
	removeRecipient(recipientId);
	return response;
}

export async function acknowledgeNotification(notification) {
	const recipientId = notification && notification.recipientId;
	if (!recipientId) {
		throw new Error("Notification recipient id is required.");
	}
	const timestamp = getTimestamp();
	const response = await sendAuthorizedRequest("put", resolveNotificationEndpoint("acknowledge", recipientId));
	updateRecipient(recipientId, {
		readAt: notification.readAt || timestamp,
		acknowledgedAt: notification.acknowledgedAt || timestamp
	});
	return response;
}

export async function markAllNotificationsRead() {
	const response = await sendAuthorizedRequest("put", resolveNotificationEndpoint("readAll"));
	const readAt = getTimestamp();
	const items = getNotificationItems().map((item) => {
		return {
			...item,
			readAt: item.readAt || readAt
		};
	});
	setNotificationState({
		items,
		unreadCount: 0
	});
	return response;
}

export async function getNotificationPushDevices(userSubId) {
	const endpoints = getNotificationEndpoints();
	if (!endpoints.admin || !endpoints.admin.pushNotificationDevices) {
		throw new Error("Notification device admin endpoint is not configured for this environment.");
	}
	return await getAuthorizedRequest(endpoints.admin.pushNotificationDevices(userSubId));
}

function getNotificationAdminEndpoint(endpointName, ...args) {
	const endpoints = getNotificationEndpoints();
	if (!endpoints.admin || !endpoints.admin[endpointName]) {
		throw new Error(`Notification admin endpoint is not configured: ${endpointName}`);
	}

	const endpoint = endpoints.admin[endpointName];
	return typeof endpoint === "function" ? endpoint(...args) : endpoint;
}

function getNotificationWorkflowEndpoint(endpointName, ...args) {
	const endpoints = getNotificationEndpoints();
	if (!endpoints.workflow || !endpoints.workflow[endpointName]) {
		throw new Error(`Notification workflow endpoint is not configured: ${endpointName}`);
	}

	const endpoint = endpoints.workflow[endpointName];
	return typeof endpoint === "function" ? endpoint(...args) : endpoint;
}

export async function createNotificationSchedule(schedule) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("schedules"), schedule);
}

export async function listNotificationSchedules(params = {}) {
	return await getAuthorizedRequest(appendQueryString(getNotificationAdminEndpoint("schedules"), params));
}

export async function evaluateDueNotificationSchedules(params = {}) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("evaluateDueSchedules"), params);
}

export async function runNotificationScheduleNow(scheduleId) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("runScheduleNow", scheduleId));
}

export async function updateNotificationScheduleStatus(scheduleId, status) {
	return await sendAuthorizedRequest("put", getNotificationAdminEndpoint("scheduleStatus", scheduleId), { status });
}

export async function createActivitySchedulePlan(plan) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("activitySchedulePlans"), plan);
}

export async function listActivitySchedulePlans(params = {}) {
	return await getAuthorizedRequest(appendQueryString(getNotificationAdminEndpoint("activitySchedulePlans"), params));
}

export async function evaluateDueActivitySchedulePlans(params = {}) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("evaluateDueActivitySchedulePlans"), params);
}

export async function updateActivitySchedulePlanStatus(planId, status) {
	return await sendAuthorizedRequest("put", getNotificationAdminEndpoint("activitySchedulePlanStatus", planId), { status });
}

export async function upsertActivityScheduleScope(planId, scope) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("activitySchedulePlanScope", planId), scope);
}

export async function updateActivityScheduleScope(scopeId, scope) {
	return await sendAuthorizedRequest("put", getNotificationAdminEndpoint("activityScheduleScope", scopeId), scope);
}

export async function deleteActivityScheduleScope(scopeId) {
	return await sendAuthorizedRequest("delete", getNotificationAdminEndpoint("activityScheduleScope", scopeId));
}

export async function listActivityResponsibilityScopes(params = {}) {
	return await getAuthorizedRequest(appendQueryString(getNotificationAdminEndpoint("activityResponsibilityScopes"), params));
}

export async function upsertActivityResponsibilityScope(scope) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("activityResponsibilityScopes"), scope);
}

export async function updateActivityResponsibilityScope(scopeId, scope) {
	return await sendAuthorizedRequest("put", getNotificationAdminEndpoint("activityResponsibilityScope", scopeId), scope);
}

export async function deleteActivityResponsibilityScope(scopeId) {
	return await sendAuthorizedRequest("delete", getNotificationAdminEndpoint("activityResponsibilityScope", scopeId));
}

export async function resolveActivityResponsibilities(params = {}) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("resolveActivityResponsibilities"), params);
}

export async function createActivityWorkflowEvent(event) {
	return await sendAuthorizedRequest("post", getNotificationWorkflowEndpoint("events"), event);
}

export async function listActivityWorkflowEvents(params = {}) {
	return await getAuthorizedRequest(appendQueryString(getNotificationWorkflowEndpoint("events"), params));
}

export async function createActivityWorkflowWorkItem(workItem) {
	return await sendAuthorizedRequest("post", getNotificationWorkflowEndpoint("workItems"), workItem);
}

export async function listActivityWorkflowWorkItems(params = {}) {
	return await getAuthorizedRequest(appendQueryString(getNotificationWorkflowEndpoint("workItems"), params));
}

export async function updateActivityWorkflowWorkItemStatus(workItemId, status, reason = "") {
	return await sendAuthorizedRequest("put", getNotificationWorkflowEndpoint("workItemStatus", workItemId), { status, reason });
}

export async function updateActivityWorkflowWorkItemProgress(workItemId, payload = {}) {
	return await sendAuthorizedRequest("put", getNotificationWorkflowEndpoint("workItemProgress", workItemId), payload);
}

export async function getActivityWorkflowWorkItemOpenContext(workItemId) {
	return await getAuthorizedRequest(getNotificationWorkflowEndpoint("workItemOpenContext", workItemId));
}

export async function createWorkflowNotificationPolicy(policy) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("workflowPolicies"), policy);
}

export async function listWorkflowNotificationPolicies(params = {}) {
	return await getAuthorizedRequest(appendQueryString(getNotificationAdminEndpoint("workflowPolicies"), params));
}

export async function evaluateDueWorkflowWorkItems(params = {}) {
	return await sendAuthorizedRequest("post", getNotificationAdminEndpoint("evaluateDueWorkflowWorkItems"), params);
}
